<script setup lang="ts">
    import { FORM_HELPERS } from "@/components/forms/Form/helpers";
    import KInnerSection from "@/components/generic/KInnerSection.vue";
    import KSection from "@/components/generic/KSection.vue";
    import moment from "moment";
    import { computed, inject, nextTick, onMounted, ref, toRefs } from "vue";

    const lakemedelId = ref<string | null>(null);

    const props = withDefaults(
        defineProps<{
            hasSelectedLakemedel: boolean;
            fornya?: boolean;
        }>(),
        {
            fornya: false,
        }
    );

    const { fornya } = toRefs(props);

    const getRequired = computed(() => {
        return fornya.value ? "" : "required";
    });

    const behandlingsdatumInnerSection = ref();

    const formHelpers = inject<{
        getFieldValue: (props: { name: string }) => unknown;
    } | null>(FORM_HELPERS, null);

    defineExpose({
        formListeners: [
            {
                fieldNames: ["lakemedelId"],
                action: ({ values }) => {
                    lakemedelId.value = values?.lakemedelId ? (values.lakemedelId as string) : null;
                },
            },
        ] as Array<{
            fieldNames: string[];
            action: ({ values }: { values: { [key: string]: unknown } }) => void;
        }>,
    });

    /**
     * Expanderar inner sections som har data satt. Gäller vid inläsning
     */
    const toggleInnerSectionsWithValue = async () => {
        await nextTick();

        const extraData = formHelpers?.getFieldValue?.({ name: "extraData" }) as
            | {
                  senaste_datum_for_uppfoljning_av_behandling?: string;
                  senaste_datum_for_avslut_av_behandling?: string;
              }
            | undefined;

        if (
            extraData?.senaste_datum_for_uppfoljning_av_behandling ||
            extraData?.senaste_datum_for_avslut_av_behandling
        ) {
            behandlingsdatumInnerSection?.value?.toggle?.();
        }
    };

    onMounted(async () => {
        await toggleInnerSectionsWithValue();
    });
</script>

<template>
    <KSection
        :expanded="hasSelectedLakemedel"
        name="behandling"
        heading="Behandling"
        :collapsible="hasSelectedLakemedel"
        :number="3"
    >
        <div class="form-content readable-disabled">
            <KAutocomplete
                name="behandlingsorsak"
                :options="[]"
                label="Behandlingsorsak"
                :validation="getRequired"
                class="span-two"
                :disabled="fornya"
            />
            <KText name="annan_orsak" label="Annan orsak" class="span-two" disabled />
            <KText
                name="behandlingsandamal"
                label="Behandlingsändamål"
                type="textarea"
                class="span-two"
                :maxCount="256"
                suffixText="Visas i doseringsanvisning"
                :validation="getRequired + '|max:256'"
                :disabled="fornya"
            />
            <KInnerSection
                ref="behandlingsdatumInnerSection"
                name="behandlingsdatum-inner-section"
                hiddenContent="Behandlingsdatum"
                visibleContent="Behandlingsdatum"
            >
                <KDateTimepicker
                    name="senaste_datum_for_uppfoljning_av_behandling"
                    label="Senaste datum för uppföljning av behandling"
                    class="span-two"
                    suffixText="Får inte anges samtidigt som senaste datum för avslut av behandling"
                    :maxDate="moment().add(5, 'years').toDate()"
                />
                <KDateTimepicker
                    name="senaste_datum_for_avslut_av_behandling"
                    label="Senaste datum för avslut av behandling"
                    class="span-two"
                    suffixText="Får inte anges samtidigt som senaste datum för uppföljning av behandling"
                    :maxDate="moment().add(5, 'years').toDate()"
                />
            </KInnerSection>
        </div>
    </KSection>
</template>

<style scoped lang="scss">
    .behandling {
    }
</style>
